import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ChevronLeft, Eye, EyeOff } from "lucide-react";
import ContainerPage from "../../components/Containers/ContainerPage";
import Button from "../../components/Buttons/Button";
import TextField from "../../components/Form/TextField";
import Loader from "../../components/Loaders/Loader";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { getPathDatabaseByRole } from "../../actions/auth";
import {
  startAttachArtboardReference,
  startAttachOriginCreativeAndAnalyze,
  startListenerCreativeTemplate,
  startSaveCreativeTemplate,
  startUploadCreativeTemplateLayerAsset,
  stopListenerCreativeTemplate,
} from "../../actions/creativeTemplates";
import { canWriteCreativeTemplates } from "../../features/creativeTemplates/utils/permissions";
import {
  countNodes,
  applyCanvasBackgroundLayout,
  applyNodeType,
  applyOverlayBox,
  applySourceKind,
  createDefaultNode,
  deleteNodeById,
  findNodePath,
  flattenTreeToBoxes,
  getAbsolutePx,
  insertNodeAtOverlay,
  isCanvasBackgroundNode,
  listNodeNames,
  patchNodeById,
  slugifyName,
  uniqueName,
  resolveInsertParentID,
  syncVariablesFromArtboards,
} from "../../features/creativeTemplates/utils/layerTree";
import {
  applyCanvasBackgroundToArtboards,
  getArtboard,
  getArtboardReference,
  getReferenceAssetKey,
  hydrateArtboards,
  patchActiveArtboardGraph,
  resolveActiveArtboardID,
  seedArtboard,
} from "../../features/creativeTemplates/utils/artboards";
import { applySiblingDisplayOrder } from "../../features/creativeTemplates/instance/instanceGraph";
import TemplateCanvasOverlay from "../../features/creativeTemplates/editor/TemplateCanvasOverlay";
import ArtboardSwitcher from "../../features/creativeTemplates/editor/ArtboardSwitcher";
import LayerTreePanel from "../../features/creativeTemplates/editor/LayerTreePanel";
import LayerPropertiesPanel from "../../features/creativeTemplates/editor/LayerPropertiesPanel";
import { useTemplateEditorHistory } from "../../features/creativeTemplates/editor/useTemplateEditorHistory";
import { SimpleAlert } from "../../components/Alerts/Alerts";
import { getKeyDatabase } from "../../services/public";
import {
  ALERT_ICON_TYPE_SUCCESS,
  CREATIVE_TEMPLATE_ANALYSIS_STATUS,
  CREATIVE_TEMPLATE_ARTBOARD_PRESETS,
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
  FONT_SIZE_BUTTON,
  FONT_WEIGHT_BUTTON,
} from "../../utils/constants";
import {
  LOADING_INFORMATION,
  TEMPLATES_ANALYZE_ERROR,
  TEMPLATES_ANALYZE_RETRY,
  TEMPLATES_ANALYZING_LOCK,
  TEMPLATES_BACK,
  TEMPLATES_BUTTON_UPLOAD,
  TEMPLATES_DRAW,
  TEMPLATES_DRAWING,
  TEMPLATES_EMPTY_CANVAS_HINT,
  TEMPLATES_EMPTY_ADD_LAYER,
  TEMPLATES_BACKGROUND_LAYER,
  TEMPLATES_NAME,
  TEMPLATES_NEW_LAYER,
  TEMPLATES_SAVE,
  TEMPLATES_SAVE_ERROR,
  TEMPLATES_SAVE_SUCCESS,
  TEMPLATES_UNAUTHORIZED,
  TEMPLATES_UNTITLED,
  TEMPLATES_UPLOAD_ERROR,
  TEMPLATES_UPLOAD_REFERENCE,
  TEMPLATES_CHANGE_REFERENCE,
  TEMPLATES_SHOW_GUIDE,
  TEMPLATES_HIDE_GUIDE,
} from "../../i18n/keysTranslations";

const emptyInspector = {
  layerName: "",
  type: "text",
  sourceKind: "data",
  fixedValue: "",
  variable: "",
  expression: "",
  promptTemplate: "",
  assetRef: "",
  fontAssetRef: "",
  fontSize: "",
  color: "",
  align: "left",
  orientation: "horizontal",
  maximumLines: "",
  minimumLines: "",
  frameWidth: "",
  frameHeight: "",
  clipContent: "false",
  isBackground: false,
  posX: "",
  posY: "",
  posW: "",
  posH: "",
};

const inspectorFromNode = ({ node, layout }) => ({
  ...emptyInspector,
  layerName: node.name,
  type: node.type,
  sourceKind: node.source?.kind || "",
  fixedValue: node.source?.value || "",
  variable: node.source?.variable || "",
  expression: node.source?.expression || "",
  promptTemplate: node.source?.promptTemplate || "",
  assetRef: node.source?.assetRef || "",
  fontAssetRef: node.style?.fontAssetRef || "",
  fontSize: node.style?.fontSize ?? "",
  color: node.style?.color || "",
  align: node.style?.align || "left",
  orientation: node.style?.orientation || "horizontal",
  maximumLines: node.overflow?.maximumLines ?? "",
  minimumLines: node.overflow?.minimumLines ?? "",
  frameWidth: node.componentFrame?.width ?? "",
  frameHeight: node.componentFrame?.height ?? "",
  clipContent: node.clipContent ? "true" : "false",
  isBackground: Boolean(node.isBackground),
  posX: layout?.x ?? "",
  posY: layout?.y ?? "",
  posW: layout?.width ?? "",
  posH: layout?.height ?? "",
});

const isNativeEditableTarget = (target) => {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName;
  if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") {
    return true;
  }
  return target.isContentEditable;
};

const nextAssetKey = ({ name, assets, artboards, existingRef }) => {
  if (existingRef && existingRef !== "reference" && !existingRef.startsWith("reference_")) {
    return existingRef;
  }
  const reservedKeys = [
    "reference",
    ...Object.keys(artboards || {}).map((artboardID) =>
      getReferenceAssetKey({ artboardID })
    ),
  ];
  return uniqueName({
    name: slugifyName({ name, fallback: "layer" }),
    existingNames: [...Object.keys(assets || {}), ...reservedKeys],
  });
};

const TemplateEditor = ({ templateID = "", onBackToList = () => {} }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { role } = dispatch(getPathDatabaseByRole());
  const canWrite = canWriteCreativeTemplates({ role });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingReference, setIsUploadingReference] = useState(false);
  const [uploadingAssetNodeID, setUploadingAssetNodeID] = useState(null);
  const [artboards, setArtboards] = useState({});
  const [activeArtboardID, setActiveArtboardID] = useState(
    CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID
  );
  const [nodes, setNodes] = useState({});
  const [variables, setVariables] = useState({});
  const [assets, setAssets] = useState({});
  const [analysis, setAnalysis] = useState(null);
  const [selectedID, setSelectedID] = useState(null);
  const [expandedIDs, setExpandedIDs] = useState({});
  const [isDrawing, setIsDrawing] = useState(true);
  const [isGuideVisible, setIsGuideVisible] = useState(false);
  const selectedIDRef = useRef(null);
  const graphRef = useRef({ nodes: {}, nodeLayouts: {} });
  const artboardsRef = useRef({});
  const activeArtboardIDRef = useRef(CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID);
  const assetsRef = useRef({});
  const variablesRef = useRef({});
  const listenerRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const analysisStatusRef = useRef(null);
  const fileInputRef = useRef(null);
  const referenceFileInputRef = useRef(null);
  const isLockedRef = useRef(false);

  const getHistorySnapshot = () => ({
    nodes: graphRef.current.nodes,
    artboards: artboardsRef.current,
    activeArtboardID: activeArtboardIDRef.current,
    variables: variablesRef.current,
    assets: assetsRef.current,
    selectedID: selectedIDRef.current,
  });

  const applyHistorySnapshot = (snapshot) => {
    const nextArtboards = snapshot.artboards || {};
    const nextActiveID = resolveActiveArtboardID({
      artboards: nextArtboards,
      activeArtboardID: snapshot.activeArtboardID,
    });
    const nextNodes =
      nextArtboards[nextActiveID]?.nodes || snapshot.nodes || {};
    const nextLayouts = nextArtboards[nextActiveID]?.nodeLayouts || {};
    graphRef.current = {
      nodes: nextNodes,
      nodeLayouts: nextLayouts,
    };
    artboardsRef.current = nextArtboards;
    activeArtboardIDRef.current = nextActiveID;
    variablesRef.current = snapshot.variables;
    assetsRef.current = snapshot.assets;
    selectedIDRef.current = snapshot.selectedID;
    setNodes(nextNodes);
    setArtboards(nextArtboards);
    setActiveArtboardID(nextActiveID);
    setVariables(snapshot.variables);
    setAssets(snapshot.assets);
    setSelectedID(snapshot.selectedID);
  };

  const { push, endCoalesce, undo, redo, clear } = useTemplateEditorHistory({
    getSnapshot: getHistorySnapshot,
    applySnapshot: applyHistorySnapshot,
  });
  const historyClearRef = useRef(clear);
  historyClearRef.current = clear;

  const {
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      ...emptyInspector,
    },
  });

  const isAnalyzing =
    analysis?.status === CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING;
  const isAnalysisError =
    analysis?.status === CREATIVE_TEMPLATE_ANALYSIS_STATUS.ERROR;
  const isLocked = !canWrite || isAnalyzing;
  const lockMessage = isAnalyzing
    ? TEMPLATES_ANALYZING_LOCK
    : TEMPLATES_UNAUTHORIZED;
  const activeArtboard = getArtboard({
    artboards,
    artboardID: activeArtboardID,
  }) || { width: 1, height: 1, nodeLayouts: {} };
  const nodeLayouts = activeArtboard.nodeLayouts || {};
  const artboard = {
    width: activeArtboard.width || 1,
    height: activeArtboard.height || 1,
  };
  const canvasImageUrl =
    getArtboardReference({
      artboard: activeArtboard,
      artboardID: activeArtboardID,
      assets,
    })?.url || "";
  const isEmptyCanvas =
    !canvasImageUrl && countNodes({ nodes }) === 0 && !isAnalyzing;

  useEffect(() => {
    selectedIDRef.current = selectedID;
  }, [selectedID]);

  useEffect(() => {
    graphRef.current = { nodes, nodeLayouts };
  }, [nodes, nodeLayouts]);

  useEffect(() => {
    artboardsRef.current = artboards;
  }, [artboards]);

  useEffect(() => {
    activeArtboardIDRef.current = activeArtboardID;
  }, [activeArtboardID]);

  useEffect(() => {
    assetsRef.current = assets;
  }, [assets]);

  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  useEffect(() => {
    isLockedRef.current = isLocked;
  }, [isLocked]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isLockedRef.current) return;
      if (isNativeEditableTarget(event.target)) return;
      const key = event.key.toLowerCase();
      const hasModifier = event.ctrlKey || event.metaKey;
      if (!hasModifier) return;

      if (key === "z" && event.shiftKey) {
        event.preventDefault();
        redo();
        return;
      }
      if (key === "z") {
        event.preventDefault();
        undo();
        return;
      }
      if (key === "y" && event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        redo();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [redo, undo]);

  useEffect(() => {
    hasLoadedRef.current = false;
    analysisStatusRef.current = null;

    if (!templateID) {
      onBackToList();
      return undefined;
    }

    const applySnapshot = ({ card, doc, overlayActiveNodes = false }) => {
      const nextArtboards = hydrateArtboards({
        artboards: doc?.artboards || {},
        nodes: doc?.nodes || {},
      });
      const nextActiveID = resolveActiveArtboardID({
        artboards: nextArtboards,
        activeArtboardID: doc?.editor?.activeArtboardID,
      });
      const withOverlay = overlayActiveNodes
        ? patchActiveArtboardGraph({
            artboards: nextArtboards,
            artboardID: nextActiveID,
            nodes: doc?.nodes || {},
          })
        : nextArtboards;
      const nextNodes = withOverlay[nextActiveID]?.nodes || {};
      const nextLayouts = withOverlay[nextActiveID]?.nodeLayouts || {};
      const nextVariables = doc?.variables || {};
      const nextAssets = doc?.assets || {};
      reset({
        name: card?.name || doc?.name || "",
        ...emptyInspector,
      });
      graphRef.current = { nodes: nextNodes, nodeLayouts: nextLayouts };
      artboardsRef.current = withOverlay;
      activeArtboardIDRef.current = nextActiveID;
      variablesRef.current = nextVariables;
      assetsRef.current = nextAssets;
      selectedIDRef.current = null;
      setArtboards(withOverlay);
      setActiveArtboardID(nextActiveID);
      setNodes(nextNodes);
      setVariables(nextVariables);
      setAssets(nextAssets);
      setAnalysis(doc?.editor?.analysis || null);
      setSelectedID(null);
      historyClearRef.current();
    };

    dispatch(
      startListenerCreativeTemplate({
        templateID,
        onUpdatedDataCallback: (value) => {
          if (!value) {
            onBackToList();
            return;
          }
          const nextStatus = value.doc?.editor?.analysis?.status || null;
          const previousStatus = analysisStatusRef.current;
          const shouldReplaceGraph =
            !hasLoadedRef.current ||
            nextStatus === CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING ||
            previousStatus === CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING ||
            nextStatus !== previousStatus;

          if (shouldReplaceGraph) {
            applySnapshot({
              card: value.card,
              doc: value.doc,
              overlayActiveNodes:
                nextStatus === CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING ||
                previousStatus === CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING,
            });
          } else {
            setAnalysis(value.doc?.editor?.analysis || null);
          }

          analysisStatusRef.current = nextStatus;
          if (!hasLoadedRef.current) {
            hasLoadedRef.current = true;
            setIsLoading(false);
          }
        },
        onReferenceAvailable: (dbRef) => {
          listenerRef.current = dbRef;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCreativeTemplate({ listenerRef: listenerRef.current })
      );
    };
  }, [dispatch, onBackToList, reset, templateID]);

  const selectedPath = useMemo(
    () => (selectedID ? findNodePath({ nodes, nodeID: selectedID }) : null),
    [nodes, selectedID]
  );
  const selectedNode = selectedPath?.node || null;
  const selectedLayout = useMemo(
    () =>
      selectedID
        ? getAbsolutePx({
            nodes,
            nodeLayouts,
            nodeID: selectedID,
            artboardWidth: artboard.width,
            artboardHeight: artboard.height,
          })
        : null,
    [artboard.height, artboard.width, nodeLayouts, nodes, selectedID]
  );

  useEffect(() => {
    const templateName = getValues("name");
    if (!selectedNode) {
      reset({ ...getValues(), ...emptyInspector, name: templateName });
      return;
    }
    reset({
      ...getValues(),
      ...inspectorFromNode({ node: selectedNode, layout: selectedLayout }),
      name: templateName,
    });
  }, [getValues, reset, selectedID, selectedLayout, selectedNode]);

  const boxes = useMemo(() => {
    const nextBoxes = flattenTreeToBoxes({
      nodes,
      nodeLayouts,
      artboardWidth: artboard.width,
      artboardHeight: artboard.height,
    });
    return nextBoxes.map((box) => {
      const assetUrl =
        box.type === "image" && box.sourceKind === "asset" && box.assetRef
          ? assets?.[box.assetRef]?.url || ""
          : "";
      return { ...box, assetUrl };
    });
  }, [artboard.height, artboard.width, assets, nodeLayouts, nodes]);

  const selectedAssetUrl =
    selectedNode?.source?.kind === "asset" && selectedNode.source?.assetRef
      ? assets?.[selectedNode.source.assetRef]?.url || ""
      : "";

  const commitGraph = ({
    nodes: nextNodes,
    nodeLayouts: nextLayouts,
    artboards: nextArtboards,
  }) => {
    const activeID = activeArtboardIDRef.current;
    const baseArtboards = nextArtboards || artboardsRef.current;
    const resolvedLayouts =
      nextLayouts ?? baseArtboards[activeID]?.nodeLayouts ?? {};
    const resolvedArtboards = patchActiveArtboardGraph({
      artboards: baseArtboards,
      artboardID: activeID,
      nodes: nextNodes,
      nodeLayouts: resolvedLayouts,
    });
    const nextVariables = syncVariablesFromArtboards({
      artboards: resolvedArtboards,
      variables: variablesRef.current,
    });
    graphRef.current = { nodes: nextNodes, nodeLayouts: resolvedLayouts };
    artboardsRef.current = resolvedArtboards;
    variablesRef.current = nextVariables;
    setNodes(nextNodes);
    setArtboards(resolvedArtboards);
    setVariables(nextVariables);
  };

  const onSelect = (id) => {
    setSelectedID(id);
    setIsDrawing(false);
  };

  const onCreateBox = ({ nx, ny, nw, nh }) => {
    const { nodes: currentNodes, nodeLayouts: currentLayouts } =
      graphRef.current;
    const parentID = resolveInsertParentID({
      nodes: currentNodes,
      selectedID: selectedIDRef.current,
    });
    const id = dispatch(getKeyDatabase());
    const node = createDefaultNode({
      id,
      name: t(TEMPLATES_NEW_LAYER),
      existingNames: listNodeNames({ nodes: currentNodes }),
    });
    const result = insertNodeAtOverlay({
      nodes: currentNodes,
      nodeLayouts: currentLayouts,
      parentID,
      node,
      nx,
      ny,
      nw,
      nh,
      artboardWidth: artboard.width,
      artboardHeight: artboard.height,
    });
    if (!result.ok) return;
    push();
    commitGraph(result);
    selectedIDRef.current = id;
    setSelectedID(id);
    setIsDrawing(false);
    if (parentID) {
      setExpandedIDs((current) => ({ ...current, [parentID]: true }));
    }
  };

  const onUpdateBox = ({ id, nx, ny, nw, nh }) => {
    const { nodes: currentNodes, nodeLayouts: currentLayouts } =
      graphRef.current;
    const currentAbs = getAbsolutePx({
      nodes: currentNodes,
      nodeLayouts: currentLayouts,
      nodeID: id,
      artboardWidth: artboard.width,
      artboardHeight: artboard.height,
    });
    const result = applyOverlayBox({
      nodes: currentNodes,
      nodeLayouts: currentLayouts,
      nodeID: id,
      nx: nx ?? currentAbs.x / artboard.width,
      ny: ny ?? currentAbs.y / artboard.height,
      nw: nw ?? currentAbs.width / artboard.width,
      nh: nh ?? currentAbs.height / artboard.height,
      artboardWidth: artboard.width,
      artboardHeight: artboard.height,
    });
    push({ coalesceKey: `transform:${id}` });
    commitGraph(result);
  };

  const onAddLayer = () => {
    onCreateBox({ nx: 0.08, ny: 0.08, nw: 0.28, nh: 0.18 });
  };

  const onAddFullCanvasLayer = () => {
    const { nodes: currentNodes, nodeLayouts: currentLayouts } =
      graphRef.current;
    const id = dispatch(getKeyDatabase());
    const node = applySourceKind({
      node: createDefaultNode({
        id,
        name: t(TEMPLATES_BACKGROUND_LAYER),
        existingNames: listNodeNames({ nodes: currentNodes }),
        type: "image",
      }),
      kind: "generated",
    });
    const result = insertNodeAtOverlay({
      nodes: currentNodes,
      nodeLayouts: currentLayouts,
      parentID: null,
      node,
      nx: 0,
      ny: 0,
      nw: 1,
      nh: 1,
      artboardWidth: artboard.width,
      artboardHeight: artboard.height,
    });
    if (!result.ok) return;
    push();
    commitGraph(result);
    selectedIDRef.current = id;
    setSelectedID(id);
    setIsDrawing(false);
  };

  const onAnalyzeCreative = async ({ file } = {}) => {
    if (isLocked && !isAnalysisError) return;
    if (file) {
      setIsUploading(true);
      setIsGuideVisible(false);
    }
    const response = await dispatch(
      startAttachOriginCreativeAndAnalyze({ templateID, file })
    );
    setIsUploading(false);
    if (!response.ok && response.reason !== "analyzeFailed") {
      SimpleAlert({
        title: t(
          response.reason === "unauthorized"
            ? TEMPLATES_UNAUTHORIZED
            : TEMPLATES_UPLOAD_ERROR
        ),
        icon: "error",
      });
    }
  };

  const onUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await onAnalyzeCreative({ file });
  };

  const onUploadReference = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || isLocked) return;
    const artboardID = activeArtboardIDRef.current;
    const flushedArtboards = patchActiveArtboardGraph({
      artboards: artboardsRef.current,
      artboardID,
      nodes: graphRef.current.nodes,
      nodeLayouts: graphRef.current.nodeLayouts,
    });
    artboardsRef.current = flushedArtboards;
    const currentArtboard = flushedArtboards[artboardID];
    if (!currentArtboard) return;

    setIsUploadingReference(true);
    const response = await dispatch(
      startAttachArtboardReference({
        templateID,
        artboardID,
        file,
        artboard: currentArtboard,
        artboards: flushedArtboards,
      })
    );
    setIsUploadingReference(false);
    if (!response.ok) {
      SimpleAlert({
        title: t(
          response.reason === "unauthorized"
            ? TEMPLATES_UNAUTHORIZED
            : TEMPLATES_UPLOAD_ERROR
        ),
        icon: "error",
      });
      return;
    }

    const nextAssets = {
      ...assetsRef.current,
      [response.data.assetKey]: response.data.asset,
    };
    const nextArtboards = {
      ...artboardsRef.current,
      [artboardID]: {
        ...artboardsRef.current[artboardID],
        referenceAssetRef: response.data.assetKey,
      },
    };
    assetsRef.current = nextAssets;
    artboardsRef.current = nextArtboards;
    setAssets(nextAssets);
    setArtboards(nextArtboards);
    setIsGuideVisible(true);
  };

  const onUploadLayerAsset = async ({ file } = {}) => {
    if (isLocked || !file) return;
    const currentID = selectedIDRef.current;
    if (!currentID) return;

    setUploadingAssetNodeID(currentID);
    const response = await dispatch(
      startUploadCreativeTemplateLayerAsset({ templateID, file })
    );
    setUploadingAssetNodeID(null);
    if (!response.ok) {
      SimpleAlert({
        title: t(
          response.reason === "unauthorized"
            ? TEMPLATES_UNAUTHORIZED
            : TEMPLATES_UPLOAD_ERROR
        ),
        icon: "error",
      });
      return;
    }

    const { nodes: currentNodes, nodeLayouts: currentLayouts } =
      graphRef.current;
    const found = findNodePath({ nodes: currentNodes, nodeID: currentID });
    if (!found) return;

    const assetKey = nextAssetKey({
      name: found.node.name,
      assets: assetsRef.current,
      artboards: artboardsRef.current,
      existingRef: found.node.source?.assetRef,
    });
    push();
    const nextAssets = {
      ...assetsRef.current,
      [assetKey]: {
        type: "image",
        assetID: response.data.assetID,
        mimeType: response.data.mimeType,
        url: response.data.url,
      },
    };
    assetsRef.current = nextAssets;
    setAssets(nextAssets);
    commitGraph({
      nodeLayouts: currentLayouts,
      nodes: patchNodeById({
        nodes: currentNodes,
        nodeID: currentID,
        patch: {
          source: { kind: "asset", assetRef: assetKey },
        },
      }),
    });
  };

  const onChangeField = ({ key, value }) => {
    const currentID = selectedIDRef.current;
    if (!currentID) return;
    const { nodes: currentNodes, nodeLayouts: currentLayouts } =
      graphRef.current;
    const found = findNodePath({ nodes: currentNodes, nodeID: currentID });
    if (!found) return;
    push({ coalesceKey: `field:${currentID}:${key}` });
    const layout = getAbsolutePx({
      nodes: currentNodes,
      nodeLayouts: currentLayouts,
      nodeID: currentID,
      artboardWidth: artboard.width,
      artboardHeight: artboard.height,
    });

    if (key === "type") {
      commitGraph({
        nodeLayouts: currentLayouts,
        nodes: patchNodeById({
          nodes: currentNodes,
          nodeID: currentID,
          patch: applyNodeType({
            node: found.node,
            type: value,
            layout,
          }),
        }),
      });
      return;
    }
    if (key === "sourceKind") {
      commitGraph({
        nodeLayouts: currentLayouts,
        nodes: patchNodeById({
          nodes: currentNodes,
          nodeID: currentID,
          patch: applySourceKind({ node: found.node, kind: value }),
        }),
      });
      return;
    }
    if (key === "isBackground") {
      const flushed = patchActiveArtboardGraph({
        artboards: artboardsRef.current,
        artboardID: activeArtboardIDRef.current,
        nodes: currentNodes,
        nodeLayouts: currentLayouts,
      });
      const nextArtboards = applyCanvasBackgroundToArtboards({
        artboards: flushed,
        nodeID: currentID,
        isBackground: Boolean(value),
      });
      const active = nextArtboards[activeArtboardIDRef.current] || {};
      commitGraph({
        nodes: active.nodes || {},
        nodeLayouts: active.nodeLayouts || {},
        artboards: nextArtboards,
      });
      return;
    }
    if (["posX", "posY", "posW", "posH"].includes(key)) {
      if (
        isCanvasBackgroundNode({
          node: found.node,
          parentID: found.parentID,
        })
      ) {
        commitGraph({
          nodes: currentNodes,
          nodeLayouts: applyCanvasBackgroundLayout({
            nodeLayouts: currentLayouts,
            nodeID: currentID,
            width: artboard.width,
            height: artboard.height,
          }),
        });
        return;
      }
      const next = {
        x: key === "posX" ? value : layout.x,
        y: key === "posY" ? value : layout.y,
        width: key === "posW" ? value : layout.width,
        height: key === "posH" ? value : layout.height,
      };
      commitGraph(
        applyOverlayBox({
          nodes: currentNodes,
          nodeLayouts: currentLayouts,
          nodeID: currentID,
          nx: next.x / artboard.width,
          ny: next.y / artboard.height,
          nw: next.width / artboard.width,
          nh: next.height / artboard.height,
          artboardWidth: artboard.width,
          artboardHeight: artboard.height,
        })
      );
      return;
    }

    let patch = {};
    if (key === "name") patch = { name: value };
    if (key === "fixedValue") {
      patch = { source: { ...found.node.source, kind: "fixed", value } };
    }
    if (key === "variable") {
      patch = { source: { ...found.node.source, kind: "data", variable: value } };
    }
    if (key === "expression") {
      patch = {
        source: { ...found.node.source, kind: "computed", expression: value },
      };
    }
    if (key === "promptTemplate") {
      const nextSource = {
        ...(found.node.source || {}),
        kind: "generated",
        generator: found.node.type === "text" ? "text" : "image",
        promptTemplate: value,
      };
      delete nextSource.negativePrompt;
      patch = { source: nextSource };
    }
    if (key === "assetRef") {
      patch = {
        source: { ...found.node.source, kind: "asset", assetRef: value || null },
      };
    }
    if (
      key === "fontAssetRef" ||
      key === "fontSize" ||
      key === "color" ||
      key === "align" ||
      key === "orientation"
    ) {
      patch = { style: { ...(found.node.style || {}), [key]: value } };
    }
    if (key === "maximumLines" || key === "minimumLines") {
      patch = {
        overflow: {
          ...(found.node.overflow || {}),
          strategy: "shrink",
          [key]: value,
        },
      };
    }
    if (key === "frameWidth" || key === "frameHeight") {
      patch = {
        componentFrame: {
          ...(found.node.componentFrame || {}),
          width:
            key === "frameWidth"
              ? value
              : found.node.componentFrame?.width || 200,
          height:
            key === "frameHeight"
              ? value
              : found.node.componentFrame?.height || 200,
        },
      };
    }
    if (key === "clipContent") patch = { clipContent: value };

    commitGraph({
      nodeLayouts: currentLayouts,
      nodes: patchNodeById({
        nodes: currentNodes,
        nodeID: currentID,
        patch,
      }),
    });
  };

  const onSelectArtboard = (artboardID) => {
    if (!artboardsRef.current[artboardID]) return;
    const flushed = patchActiveArtboardGraph({
      artboards: artboardsRef.current,
      artboardID: activeArtboardIDRef.current,
      nodes: graphRef.current.nodes,
      nodeLayouts: graphRef.current.nodeLayouts,
    });
    const nextArtboard = flushed[artboardID] || {};
    const nextNodes = nextArtboard.nodes || {};
    const nextLayouts = nextArtboard.nodeLayouts || {};
    artboardsRef.current = flushed;
    activeArtboardIDRef.current = artboardID;
    graphRef.current = {
      nodes: nextNodes,
      nodeLayouts: nextLayouts,
    };
    setArtboards(flushed);
    setActiveArtboardID(artboardID);
    setNodes(nextNodes);
    if (
      selectedIDRef.current &&
      !findNodePath({ nodes: nextNodes, nodeID: selectedIDRef.current })
    ) {
      selectedIDRef.current = null;
      setSelectedID(null);
    }
  };

  const onAddArtboardPreset = (presetID) => {
    const preset = CREATIVE_TEMPLATE_ARTBOARD_PRESETS[presetID];
    if (!preset || artboardsRef.current[preset.id]) return;
    const flushed = patchActiveArtboardGraph({
      artboards: artboardsRef.current,
      artboardID: activeArtboardIDRef.current,
      nodes: graphRef.current.nodes,
      nodeLayouts: graphRef.current.nodeLayouts,
    });
    const fromArtboard =
      flushed[activeArtboardIDRef.current] || activeArtboard;
    artboardsRef.current = flushed;
    setArtboards(flushed);
    push();
    const seeded = seedArtboard({ fromArtboard, preset });
    const nextArtboards = {
      ...flushed,
      [preset.id]: seeded,
    };
    artboardsRef.current = nextArtboards;
    activeArtboardIDRef.current = preset.id;
    graphRef.current = {
      nodes: seeded.nodes || {},
      nodeLayouts: seeded.nodeLayouts || {},
    };
    setArtboards(nextArtboards);
    setActiveArtboardID(preset.id);
    setNodes(seeded.nodes || {});
    selectedIDRef.current = null;
    setSelectedID(null);
  };

  const onRemoveActiveArtboard = () => {
    const currentID = activeArtboardIDRef.current;
    const currentArtboards = artboardsRef.current;
    if (Object.keys(currentArtboards).length <= 1) return;
    if (!currentArtboards[currentID]) return;
    push();
    const removed = currentArtboards[currentID];
    const nextArtboards = { ...currentArtboards };
    delete nextArtboards[currentID];
    const nextActiveID = resolveActiveArtboardID({
      artboards: nextArtboards,
      activeArtboardID: null,
    });
    const removedRefKey =
      removed?.referenceAssetRef ||
      getReferenceAssetKey({ artboardID: currentID });
    let nextAssets = assetsRef.current;
    if (removedRefKey && removedRefKey !== "reference") {
      nextAssets = { ...assetsRef.current };
      delete nextAssets[removedRefKey];
      assetsRef.current = nextAssets;
      setAssets(nextAssets);
    }
    artboardsRef.current = nextArtboards;
    activeArtboardIDRef.current = nextActiveID;
    graphRef.current = {
      nodes: nextArtboards[nextActiveID]?.nodes || {},
      nodeLayouts: nextArtboards[nextActiveID]?.nodeLayouts || {},
    };
    setArtboards(nextArtboards);
    setActiveArtboardID(nextActiveID);
    setNodes(nextArtboards[nextActiveID]?.nodes || {});
    selectedIDRef.current = null;
    setSelectedID(null);
  };

  const onSave = async () => {
    if (isLocked) {
      SimpleAlert({
        title: t(TEMPLATES_UNAUTHORIZED),
        text: t(lockMessage),
        icon: "warning",
      });
      return { ok: false, reason: "unauthorized" };
    }
    setIsSaving(true);
    const flushedArtboards = patchActiveArtboardGraph({
      artboards: artboardsRef.current,
      artboardID: activeArtboardIDRef.current,
      nodes: graphRef.current.nodes,
      nodeLayouts: graphRef.current.nodeLayouts,
    });
    artboardsRef.current = flushedArtboards;
    const response = await dispatch(
      startSaveCreativeTemplate({
        templateID,
        name: getValues("name") || t(TEMPLATES_UNTITLED),
        nodes: graphRef.current.nodes,
        variables: syncVariablesFromArtboards({
          artboards: flushedArtboards,
          variables,
        }),
        assets,
        artboards: flushedArtboards,
        activeArtboardID: activeArtboardIDRef.current,
      })
    );
    setIsSaving(false);
    if (!response.ok) {
      console.error("startSaveCreativeTemplate failed", response);
      SimpleAlert({
        title: t(TEMPLATES_SAVE_ERROR),
        text:
          response.reason === "unauthorized"
            ? t(TEMPLATES_UNAUTHORIZED)
            : t(TEMPLATES_SAVE_ERROR),
        icon: "error",
      });
      return response;
    }
    SimpleAlert({
      title: t(TEMPLATES_SAVE_SUCCESS),
      icon: ALERT_ICON_TYPE_SUCCESS,
    });
    return response;
  };

  if (isLoading) {
    return <Loader hasMessage message={t(LOADING_INFORMATION)} />;
  }

  return (
    <ContainerPage hasMarginBottom={false} sx={{ height: "100%", pt: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          width: "100%",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={onBackToList}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 0.5,
              }}
            >
              <ChevronLeft size={16} />
              <Typography
                sx={{
                  fontSize: FONT_SIZE_BUTTON,
                  fontWeight: FONT_WEIGHT_BUTTON,
                }}
              >
                {t(TEMPLATES_BACK)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ width: { xs: "100%", sm: 360 }, mt: 1.5 }}>
            <TextField
              control={control}
              errors={errors}
              name="name"
              label={t(TEMPLATES_NAME)}
              variant="filled"
              fullWidth
              disabled={isLocked}
              rules={{ required: false }}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onUpload}
          />
          <input
            ref={referenceFileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onUploadReference}
          />
          <Button
            color="secondary"
            onClick={onSave}
            disabled={isLocked}
            loading={isSaving}
          >
            {t(TEMPLATES_SAVE)}
          </Button>
        </Box>
      </Box>
      {isLocked ? (
        <Typography sx={{ mb: 2 }} color="warning.main">
          {t(lockMessage)}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "300px minmax(0, 1fr) 320px" },
          gap: 2,
          width: "100%",
          alignItems: "stretch",
        }}
      >
        <LayerTreePanel
          nodes={nodes}
          nodeLayouts={nodeLayouts}
          assets={assets}
          selectedID={selectedID}
          expandedIDs={expandedIDs}
          disabled={isLocked}
          onSelect={onSelect}
          onToggleExpand={(id) =>
            setExpandedIDs((current) => ({
              ...current,
              [id]: !current[id],
            }))
          }
          onRename={(id, name) => {
            const trimmed = String(name || "").trim();
            if (!trimmed) return;
            push();
            commitGraph({
              nodes: patchNodeById({
                nodes: graphRef.current.nodes,
                nodeID: id,
                patch: { name: trimmed },
              }),
            });
          }}
          onReorder={({ parentID, orderedDisplayIds }) => {
            push();
            commitGraph(
              applySiblingDisplayOrder({
                nodes: graphRef.current.nodes,
                nodeLayouts: graphRef.current.nodeLayouts,
                parentID,
                orderedDisplayIds,
              })
            );
          }}
          onDelete={(id) => {
            push();
            commitGraph(
              deleteNodeById({
                nodes: graphRef.current.nodes,
                nodeLayouts: graphRef.current.nodeLayouts,
                nodeID: id,
              })
            );
            if (selectedID === id) {
              selectedIDRef.current = null;
              setSelectedID(null);
            }
          }}
          onAddLayer={onAddLayer}
        />
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #E4E4E7",
            borderRadius: "14px",
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Button
              color="secondary"
              onClick={() => setIsDrawing((value) => !value)}
              disabled={isLocked || isEmptyCanvas}
            >
              {t(isDrawing ? TEMPLATES_DRAWING : TEMPLATES_DRAW)}
            </Button>
            <ArtboardSwitcher
              artboards={artboards}
              activeArtboardID={activeArtboardID}
              disabled={isLocked}
              onSelect={onSelectArtboard}
              onAddPreset={onAddArtboardPreset}
              onRemoveActive={onRemoveActiveArtboard}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                color="secondary"
                iconOnly
                ariaLabel={t(
                  isGuideVisible ? TEMPLATES_HIDE_GUIDE : TEMPLATES_SHOW_GUIDE
                )}
                tooltip={t(
                  isGuideVisible ? TEMPLATES_HIDE_GUIDE : TEMPLATES_SHOW_GUIDE
                )}
                disabled={!canvasImageUrl}
                startIcon={
                  isGuideVisible ? <Eye size={18} /> : <EyeOff size={18} />
                }
                onClick={() => setIsGuideVisible((value) => !value)}
              />
              <Button
                color="secondary"
                onClick={() => referenceFileInputRef.current?.click()}
                loading={isUploadingReference}
                disabled={isLocked}
              >
                {t(
                  canvasImageUrl
                    ? TEMPLATES_CHANGE_REFERENCE
                    : TEMPLATES_UPLOAD_REFERENCE
                )}
              </Button>
            </Box>
          </Box>
          {isEmptyCanvas ? (
            <Box
              sx={{
                minHeight: 360,
                border: "1px dashed #D4D4D8",
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                px: 3,
                textAlign: "center",
                bgcolor: "#FAFAFA",
              }}
            >
              <Typography color="text.secondary" sx={{ maxWidth: 420 }}>
                {t(TEMPLATES_EMPTY_CANVAS_HINT)}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <Button
                  color="secondary"
                  onClick={onAddFullCanvasLayer}
                  disabled={!canWrite || isLocked}
                >
                  {t(TEMPLATES_EMPTY_ADD_LAYER)}
                </Button>
                <Button
                  color="primary"
                  onClick={() => fileInputRef.current?.click()}
                  loading={isUploading}
                  disabled={!canWrite}
                >
                  {t(TEMPLATES_BUTTON_UPLOAD)}
                </Button>
              </Box>
            </Box>
          ) : (
            <TemplateCanvasOverlay
              imageUrl={canvasImageUrl}
              showGuide={isGuideVisible}
              artboardWidth={artboard.width}
              artboardHeight={artboard.height}
              boxes={boxes}
              selectedID={selectedID}
              isDrawing={isDrawing}
              isLocked={isLocked}
              isScanning={isAnalyzing}
              analysisError={isAnalysisError ? t(TEMPLATES_ANALYZE_ERROR) : ""}
              retryLabel={t(TEMPLATES_ANALYZE_RETRY)}
              onRetry={
                canWrite && !isAnalyzing
                  ? () => onAnalyzeCreative()
                  : undefined
              }
              onSelectBox={onSelect}
              onCreateBox={onCreateBox}
              onUpdateBox={onUpdateBox}
              onEndBoxTransform={endCoalesce}
            />
          )}
        </Box>
        <LayerPropertiesPanel
          control={control}
          errors={errors}
          disabled={isLocked || !selectedNode}
          selectedNode={selectedNode}
          isRootLayer={!selectedPath?.parentID}
          assetUrl={selectedAssetUrl}
          isUploadingAsset={uploadingAssetNodeID === selectedID}
          onChangeField={onChangeField}
          onUploadAsset={onUploadLayerAsset}
        />
      </Box>
    </ContainerPage>
  );
};

export default TemplateEditor;
